import { type SupplierListItem } from '@choco-admin/types';
import { Button } from '@heroui/react';
import React, { useCallback } from 'react';
import { FiUsers, FiMessageSquare, FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { BaseButton } from '../../../../../components/BaseButton';
import { CopyableIdChip } from '../../../../../components/CopyableIdChip';
import { AccessPackagePills } from '../../SupplierList/components/AccessPackagePills';

/** Enough of a UUID to tell two of them apart at a glance. */
const UUID_PREFIX_LENGTH = 8;

interface SupplierHeaderProps {
  supplier: SupplierListItem;
}

export const SupplierHeader: React.FC<SupplierHeaderProps> = ({ supplier }) => {
  const navigate = useNavigate();

  const handleShowUsers = useCallback(() => {
    if (supplier.id) {
      navigate({
        pathname: '/users',
        search: `?search=${encodeURIComponent(supplier.id)}`,
      });
    }
  }, [navigate, supplier.id]);

  const handleShowChats = useCallback(() => {
    if (supplier.botUserId) {
      navigate({
        pathname: '/chats',
        search: `?search=${encodeURIComponent(
          supplier.botUserId
        )}&type=all&enabledState=enabled`,
      });
    }
  }, [navigate, supplier.botUserId]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-4">
        <BaseButton
          variant="light"
          startContent={<FiArrowLeft size={16} />}
          onPress={handleBack}
        >
          Back
        </BaseButton>
        <div className="flex flex-col gap-2">
          <h1 className="title-large">
            {supplier.name || 'Unnamed Distributor'}
          </h1>
          {/* Every chip is labelled, because "00HHA" or a bare UUID on its own
              says nothing about what it is or what it is for. */}
          <div className="flex flex-wrap items-center gap-2">
            <CopyableIdChip
              label="Ref"
              name="reference ID"
              value={supplier.referenceId}
              testId="supplier-reference-id-copy"
            />
            <CopyableIdChip
              label="Distributor ID"
              name="distributor ID"
              value={supplier.id}
              shortenTo={UUID_PREFIX_LENGTH}
              testId="supplier-id-copy"
            />
            <CopyableIdChip
              label="Bot User ID"
              name="bot user ID"
              value={supplier.botUserId}
              shortenTo={UUID_PREFIX_LENGTH}
              testId="supplier-bot-user-id-copy"
            />
            {/* The same pills the distributor list uses, so a package reads the
                same in both places. */}
            <AccessPackagePills
              accessPackage={supplier.accessPackage || 'free'}
              isIntegrated={Boolean(supplier.isIntegrated)}
              direction="row"
            />
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          variant="bordered"
          startContent={<FiUsers size={16} />}
          onPress={handleShowUsers}
          isDisabled={!supplier.id}
        >
          Show Users
        </Button>
        <Button
          variant="bordered"
          startContent={<FiMessageSquare size={16} />}
          onPress={handleShowChats}
          isDisabled={!supplier.botUserId}
        >
          Show Chats
        </Button>
      </div>
    </div>
  );
};
